import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Settings2, Inbox, Gem, ConciergeBell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import { useBrand } from "../context/BrandContext";
import SettingsModalView from "./views/SettingsModalView";

interface HeaderProps {
  showItems?: boolean;
  itemsNumber?: string;
  showSettings?: boolean;
  showEmailAddress?: boolean;
  emailAddress?: string;
  isMember?: boolean;
  isGuest?: boolean;
  animateIcon?: boolean;
  animateInbox?: boolean;
  onItemsClick?: () => void;
  selectedRewardValue?: string; // Show specific reward value instead of cycling
  logoColorOverride?: 'light' | 'dark' | 'auto'; // Override logo color for special backgrounds
}

const Header: React.FC<HeaderProps> = ({
  showItems = true,
  itemsNumber = "1",
  showSettings = true,
  showEmailAddress = false,
  emailAddress = "",
  isMember = false,
  isGuest = false,
  animateIcon = false,
  animateInbox = false,
  onItemsClick,
  selectedRewardValue,
  logoColorOverride = 'auto',
}) => {
  const navigate = useNavigate();
  const { state } = useAppContext();
  const { brandConfig, isDarkMode } = useBrand();
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [currentRewardIndex, setCurrentRewardIndex] = useState(0);
  const [isEmailMasked, setIsEmailMasked] = useState(true);

  // Determine logo source based on override
  const getLogoSrc = () => {
    if (logoColorOverride === 'light') {
      return brandConfig.logos.main.light; // Always use light logo
    } else if (logoColorOverride === 'dark') {
      return brandConfig.logos.main.dark; // Always use dark logo
    } else {
      // Auto mode - use current theme
      return brandConfig.logos.main[isDarkMode ? 'dark' : 'light'];
    }
  };

  // Calculate total rewards
  const totalRewards = state.empties.reduce(
    (totals, empty) => {
      if (empty.product.isBranded && empty.product.rewards) {
        totals.points += empty.product.rewards.loyaltyPoints * empty.quantity;
        const dollarAmount = parseFloat(
          empty.product.rewards.dollarValue.replace("$", "")
        );
        totals.dollars += dollarAmount * empty.quantity;
      }
      return totals;
    },
    { points: 0, dollars: 0 }
  );

  // Check if there are any eligible items with rewards
  const hasEligibleItems = state.empties.some(empty =>
    empty.product.isBranded && empty.product.rewards
  );

  // Create reward values array with conditional fallbacks (points and dollars only)
  const rewardValues = [
    hasEligibleItems ? totalRewards.points : (state.empties.length === 0 ? 500 : 0),
    hasEligibleItems ? `$${totalRewards.dollars.toFixed(2)}` : (state.empties.length === 0 ? "$0.50" : "$0.00"),
  ];

  // Cycle through reward values every 4 seconds (only when no specific reward value is provided)
  useEffect(() => {
    if (selectedRewardValue) {
      // If a specific reward value is provided, don't cycle
      return;
    }

    if (showItems && parseInt(itemsNumber) > 0) {
      const interval = setInterval(() => {
        setCurrentRewardIndex((prevIndex) => (prevIndex + 1) % 2);
      }, 4000);
      return () => clearInterval(interval);
    } else {
      // When no items, show points (index 0)
      setCurrentRewardIndex(0);
    }
  }, [showItems, itemsNumber, selectedRewardValue]);

  // Function to mask email address
  const maskEmail = (email: string) => {
    if (!email || !email.includes('@')) return email;
    
    const [localPart, domain] = email.split('@');
    if (!localPart || !domain) return email;
    
    if (localPart.length <= 2) {
      return `${localPart[0]}****@${domain}`;
    }
    const firstTwo = localPart.substring(0, 2);
    return `${firstTwo}****@${domain}`;
  };

  const handleEmailClick = () => {
    setIsEmailMasked(!isEmailMasked);
  };

  const handleSettingsClick = () => {
    setShowSettingsModal(true);
  };

  const handleSettingsClose = () => {
    setShowSettingsModal(false);
  };

  return (
    <div
      className="box-border content-stretch flex flex-row items-start justify-between p-0 relative w-full max-h-[260px] z-[10002]"
      data-name="Header"
      data-node-id="102:240"
    >
      <div className="w-[148px] relative z-[10002]">
        {showSettings && (
          <button
            onClick={handleSettingsClick}
            className="bg-brand-surface opacity-60 backdrop-blur-md box-border content-stretch flex flex-row gap-2.5 items-center justify-center p-0 relative rounded-full shrink-0 w-20 h-20 hover:bg-brand-surface transition-colors duration-200 cursor-pointer"
            data-name="Left"
            data-node-id="102:221"
          >
            <div
              className="relative shrink-0 w-10 h-10"
              data-name="settings-2 1"
              data-node-id="102:222"
            >
              <Settings2 size={40} stroke="var(--brand-text-muted)" strokeWidth={1.5} />
            </div>
          </button>
        )}
      </div>

      <div className="flex flex-col items-center justify-center">
        <AnimatePresence>
          {emailAddress && (
            <motion.div
              onClick={handleEmailClick}
              className="flex flex-row items-center justify-center text-brand-text-primary text-lg text-center bg-brand-background border border-brand-border rounded-full px-4 py-1 cursor-pointer hover:bg-brand-surface transition-colors duration-200"
              data-name="Left Spacer"
              data-node-id="102:278"
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              layout
              transition={{
                layout: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] },
                duration: 0.5,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              <span className="whitespace-nowrap">
                {isEmailMasked ? maskEmail(emailAddress) : emailAddress}
              </span>
              {(isMember || isGuest) && (
                <motion.div
                  className="ml-2"
                  initial={
                    animateIcon
                      ? { width: 0, opacity: 0, marginLeft: 0 }
                      : false
                  }
                  animate={
                    animateIcon
                      ? { width: "auto", opacity: 1, marginLeft: 8 }
                      : {}
                  }
                  transition={{
                    duration: 0.6,
                    delay: 0.2,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                >
                  <motion.div
                    initial={animateIcon ? { x: -10, scale: 0 } : false}
                    animate={animateIcon ? { x: 0, scale: 1 } : {}}
                    transition={{
                      duration: 0.5,
                      delay: 0.4,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                  >
                    {isMember && !isGuest ? (
                      <Gem size={20} stroke="var(--brand-text-primary)" strokeWidth={1.75} />
                    ) : isGuest && !isMember ? (
                      <ConciergeBell
                        size={20}
                        stroke="var(--brand-text-primary)"
                        strokeWidth={1.75}
                      />
                    ) : null}
                  </motion.div>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <div
          className="box-border content-stretch flex flex-col items-center justify-center p-0 relative self-stretch shrink-0 w-[440px] h-[260px]"
          data-name="Logo Container"
          data-node-id="102:235"
        >
          <div
            className="h-auto relative shrink-0 w-[280px]"
            data-name="Logo"
            data-node-id="102:206"
          >
            <img
              alt="Logo"
              className="block max-w-none w-full h-full object-contain"
              src={getLogoSrc()}
            />
          </div>
        </div>
      </div>
      <div className="w-[148px]">
        <AnimatePresence>
          {showItems && parseInt(itemsNumber) > 0 && (
            <motion.button
              onClick={onItemsClick}
              className="bg-brand-text-primary box-border content-stretch flex gap-2 items-center justify-center px-6 py-2 relative rounded-full shrink-0 min-w-20 h-20 hover:bg-brand-tertiary transition-colors duration-200 cursor-pointer"
              data-name="Right"
              data-node-id="102:227"
              initial={
                animateInbox
                  ? { scale: 0, opacity: 0, rotate: -180 }
                  : { scale: 1, opacity: 1, rotate: 0 }
              }
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              exit={{ scale: 0, opacity: 0, rotate: 180 }}
              transition={
                animateInbox
                  ? {
                      duration: 0.6,
                      ease: [0.34, 1.56, 0.64, 1], // Bouncy ease for spring effect
                      type: "spring",
                      stiffness: 200,
                      damping: 20,
                    }
                  : { duration: 0 }
              }
            >
              <motion.div
                className="relative shrink-0 w-8 h-8"
                data-name="Inbox"
                data-node-id="102:228"
                initial={animateInbox ? { scale: 0.5 } : { scale: 1 }}
                animate={{ scale: 1 }}
                transition={
                  animateInbox
                    ? {
                        duration: 0.4,
                        delay: 0.2,
                        ease: [0.25, 0.1, 0.25, 1],
                      }
                    : { duration: 0 }
                }
              >
                <Inbox size={32} stroke="var(--brand-background)" strokeWidth={1.5} />
              </motion.div>

              {/* Cycling Reward Value Label */}
              <motion.div
                className="flex items-center justify-center min-w-[60px] h-[24px]"
                initial={
                  animateInbox ? { opacity: 0, y: 20 } : { opacity: 1, y: 0 }
                }
                animate={{ opacity: 1, y: 0 }}
                transition={
                  animateInbox
                    ? {
                        duration: 0.5,
                        delay: 1,
                        ease: [0.25, 0.1, 0.25, 1],
                      }
                    : { duration: 0 }
                }
              >
                <AnimatePresence mode="wait">
                  <motion.span
                    key={selectedRewardValue || currentRewardIndex}
                    className="text-brand-background font-medium text-xl whitespace-nowrap leading-none flex items-center justify-center h-full"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 0.4,
                      ease: [0.25, 0.1, 0.25, 1]
                    }}
                  >
                    {selectedRewardValue || rewardValues[currentRewardIndex]}
                  </motion.span>
                </AnimatePresence>
              </motion.div>
              <motion.div
                className="absolute bg-brand-primary box-border content-stretch flex flex-col gap-2.5 items-center justify-center -right-1.5 p-0 rounded-full w-6 h-6 top-0"
                data-name="Number Badge"
                data-node-id="102:237"
                initial={
                  animateInbox ? { scale: 0, y: -10 } : { scale: 1, y: 0 }
                }
                animate={{ scale: 1, y: 0 }}
                transition={
                  animateInbox
                    ? {
                        duration: 0.5,
                        delay: 0.3,
                        ease: [0.68, -0.55, 0.265, 1.55], // Bounce effect for badge
                      }
                    : { duration: 0 }
                }
              >
                <div
                  aria-hidden="true"
                  className="absolute border-2 border-brand-background border-solid inset-[-2px] pointer-events-none rounded-full"
                />
                <motion.div
                  className="flex flex-col font-bold h-[17px] justify-center leading-none not-italic relative shrink-0 text-brand-background text-[18px] text-center w-full"
                  data-node-id="102:238"
                  initial={animateInbox ? { opacity: 0 } : { opacity: 1 }}
                  animate={{ opacity: 1 }}
                  transition={
                    animateInbox
                      ? {
                          duration: 0.3,
                          delay: 0.5,
                        }
                      : { duration: 0 }
                  }
                >
                  <p className="block leading-normal">{itemsNumber}</p>
                </motion.div>
              </motion.div>
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* Settings Modal */}
      <SettingsModalView
        isOpen={showSettingsModal}
        onClose={handleSettingsClose}
      />
    </div>
  );
};

export default Header;